import React, { useState, useEffect } from "react";
import {
  Container,
  Row,
  Col,
  Form,
  Button,
  Tabs,
  Tab,
  Table,
  Alert,
  Spinner,
  Modal,
} from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import axios from "axios";
import "./AdminPanel.css";
import { API_BASE_URL } from "../../api";

const API_BASE = `${API_BASE_URL}/admin`;

function AdminPanel() {
  const navigate = useNavigate();
  const { i18n } = useTranslation();
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [token, setToken] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");

  const [editingProfile, setEditingProfile] = useState(null);

  // Modals state
  const [showSkillModal, setShowSkillModal] = useState(false);
  const [editingSkill, setEditingSkill] = useState(null);

  const [showEducationModal, setShowEducationModal] = useState(false);
  const [editingEducation, setEditingEducation] = useState(null);

  const [showExperienceModal, setShowExperienceModal] = useState(false);
  const [editingExperience, setEditingExperience] = useState(null);

  // Force English language for admin panel
  useEffect(() => {
    i18n.changeLanguage("en");
  }, [i18n]);

  useEffect(() => {
    const savedToken = localStorage.getItem("admin-token");
    if (savedToken) {
      setToken(savedToken);
      verifyAndFetchData(savedToken);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const verifyAndFetchData = async (tkn) => {
    try {
      setLoading(true);
      const res = await axios.get(`${API_BASE}/verify`, {
        headers: { Authorization: `Bearer ${tkn}` },
      });
      if (res.data.success) {
        await fetchData(tkn);
        setIsLoggedIn(true);
      }
    } catch (err) {
      localStorage.removeItem("admin-token");
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setError("");
    try {
      setLoading(true);
      const res = await axios.post(`${API_BASE}/login`, { username, password });
      if (res.data.success) {
        const newToken = res.data.token;
        setToken(newToken);
        localStorage.setItem("admin-token", newToken);
        await fetchData(newToken);
        setIsLoggedIn(true);
      }
    } catch (err) {
      setError("Invalid username or password. Access denied.");
    } finally {
      setLoading(false);
    }
  };

  const fetchData = async (tkn) => {
    try {
      const res = await axios.get(`${API_BASE}/all`, {
        headers: { Authorization: `Bearer ${tkn}` },
      });
      setData(res.data);
      setEditingProfile(
        res.data.profile || {
          settings: { siteName: "" },
          hero: {
            title: { en: "", uz: "" },
            subtitle: { en: "", uz: "" },
            description: { en: "", uz: "" },
          },
          typingTexts: {
            en: ["Frontend Developer", "Full Stack Developer"],
            uz: ["Frontend Dasturchi", "Full Stack Dasturchi"],
          },
          about: {
            title: { en: "", uz: "" },
            content: { en: "", uz: "" },
            quote: { en: "", uz: "" },
          },
          social: {
            github: "",
            linkedin: "",
            twitter: "",
            instagram: "",
          },
          contact: {
            email: "",
            phone: "",
            telegram: "",
          },
        }
      );
    } catch (err) {
      console.error(err);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("admin-token");
    setIsLoggedIn(false);
    setToken("");
    setData(null);
  };

  const genericSave = async (endpoint, item) => {
    try {
      setLoading(true);
      const res = await axios.post(`${API_BASE}/${endpoint}`, item, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSuccessMessage(`${endpoint} saved successfully!`);
      setTimeout(() => setSuccessMessage(""), 3000);
      await fetchData(token);
      return res.data;
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const genericDelete = async (endpoint, id) => {
    if (!window.confirm("Are you sure?")) return;
    try {
      await axios.delete(`${API_BASE}/${endpoint}/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setSuccessMessage(`${endpoint} deleted successfully!`);
      setTimeout(() => setSuccessMessage(""), 3000);
      await fetchData(token);
    } catch (err) {
      setError(err.message);
    }
  };

  const handleProfileSave = async (e) => {
    e.preventDefault();
    await genericSave("profile", editingProfile);
  };

  // ===== SKILLS HANDLERS =====
  const handleSkillSave = async (e) => {
    e.preventDefault();
    await genericSave("skill", editingSkill);
    setShowSkillModal(false);
    setEditingSkill(null);
  };

  const openSkillModal = (skill = null) => {
    setEditingSkill(
      skill || { name: "", category: "Frontend", icon: "", level: 80, order: 0 }
    );
    setShowSkillModal(true);
  };

  // ===== EDUCATION HANDLERS =====
  const handleEducationSave = async (e) => {
    e.preventDefault();
    await genericSave("education", editingEducation);
    setShowEducationModal(false);
    setEditingEducation(null);
  };

  const openEducationModal = (education = null) => {
    setEditingEducation(
      education || {
        institution: "",
        degree: { en: "", uz: "" },
        description: { en: "", uz: "" },
        period: "",
        order: 0,
      }
    );
    setShowEducationModal(true);
  };

  // ===== EXPERIENCE HANDLERS =====
  const handleExperienceSave = async (e) => {
    e.preventDefault();
    await genericSave("experience", editingExperience);
    setShowExperienceModal(false);
    setEditingExperience(null);
  };

  const openExperienceModal = (experience = null) => {
    setEditingExperience(
      experience || {
        company: "",
        role: { en: "", uz: "" },
        description: { en: "", uz: "" },
        period: "",
        order: 0,
      }
    );
    setShowExperienceModal(true);
  };

  if (!isLoggedIn) {
    return (
      <div
        className="min-vh-100 d-flex align-items-center justify-content-center"
        style={{
          background:
            "linear-gradient(135deg, #0c0515 0%, #1a0a2e 50%, #0f0820 100%)",
        }}
      >
        <div
          className="admin-login-card p-5"
          style={{
            background: "rgba(20, 10, 40, 0.85)",
            border: "2px solid #c770f0",
            borderRadius: "24px",
            boxShadow:
              "0 0 60px rgba(199, 112, 240, 0.3), inset 0 1px 0 rgba(255,255,255,0.1)",
            width: "100%",
            maxWidth: "420px",
            backdropFilter: "blur(20px)",
          }}
        >
          <h2
            className="text-center mb-4"
            style={{
              color: "#c770f0",
              fontFamily: "'PT Mono', monospace",
              fontSize: "1.8rem",
              fontWeight: "400",
              letterSpacing: "2px",
            }}
          >
            Admin Login
          </h2>
          {error && (
            <Alert variant="danger" className="text-center">
              {error}
            </Alert>
          )}
          <Form onSubmit={handleLogin}>
            <Form.Group className="mb-3">
              <Form.Control
                type="text"
                placeholder="Username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                style={{
                  background: "rgba(10, 5, 20, 0.9)",
                  border: "1px solid rgba(199, 112, 240, 0.4)",
                  borderRadius: "12px",
                  color: "#fff",
                  padding: "16px 20px",
                  fontSize: "1rem",
                  transition: "all 0.3s ease",
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = "#c770f0";
                  e.target.style.boxShadow =
                    "0 0 20px rgba(199, 112, 240, 0.4)";
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = "rgba(199, 112, 240, 0.4)";
                  e.target.style.boxShadow = "none";
                }}
                required
              />
            </Form.Group>
            <Form.Group className="mb-4">
              <Form.Control
                type="password"
                placeholder="Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                style={{
                  background: "rgba(10, 5, 20, 0.9)",
                  border: "1px solid rgba(199, 112, 240, 0.4)",
                  borderRadius: "12px",
                  color: "#fff",
                  padding: "16px 20px",
                  fontSize: "1rem",
                  transition: "all 0.3s ease",
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = "#c770f0";
                  e.target.style.boxShadow =
                    "0 0 20px rgba(199, 112, 240, 0.4)";
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = "rgba(199, 112, 240, 0.4)";
                  e.target.style.boxShadow = "none";
                }}
                required
              />
            </Form.Group>
            <Button
              type="submit"
              className="w-100"
              disabled={loading}
              style={{
                background: "linear-gradient(135deg, #623686 0%, #c770f0 100%)",
                border: "none",
                borderRadius: "12px",
                padding: "14px",
                fontSize: "1rem",
                fontWeight: "600",
                letterSpacing: "1px",
                textTransform: "uppercase",
                transition: "all 0.3s ease",
              }}
              onMouseEnter={(e) => {
                e.target.style.transform = "translateY(-2px)";
                e.target.style.boxShadow =
                  "0 8px 25px rgba(199, 112, 240, 0.5)";
              }}
              onMouseLeave={(e) => {
                e.target.style.transform = "translateY(0)";
                e.target.style.boxShadow = "none";
              }}
            >
              {loading ? <Spinner animation="border" size="sm" /> : "Login"}
            </Button>
          </Form>
        </div>
      </div>
    );
  }

  if (loading && !data) {
    return (
      <Container className="text-center mt-5">
        <Spinner animation="border" variant="light" />
      </Container>
    );
  }

  return (
    <Container
      fluid
      className="admin-panel-container"
      style={{
        paddingTop: "120px",
        paddingBottom: "40px",
        paddingLeft: "20px",
        paddingRight: "20px",
      }}
    >
      <Container style={{ maxWidth: "1600px" }}>
        <Row className="mb-4">
          <Col>
            <h1 className="text-white">Admin Dashboard</h1>
            <Button variant="danger" onClick={handleLogout}>
              Logout
            </Button>
          </Col>
        </Row>

        {successMessage && <Alert variant="success">{successMessage}</Alert>}
        {error && <Alert variant="danger">{error}</Alert>}

        <Tabs defaultActiveKey="profile" className="mb-3 admin-tabs-premium">
          {/* PROFILE TAB */}
          <Tab eventKey="profile" title="Profile & Settings">
            <Form onSubmit={handleProfileSave} className="admin-form">
              <h4 className="text-white mb-3">Site Settings</h4>
              <Form.Group className="mb-3">
                <Form.Label className="text-white">Site Name (Logo)</Form.Label>
                <Form.Control
                  type="text"
                  value={editingProfile?.settings?.siteName || ""}
                  onChange={(e) =>
                    setEditingProfile({
                      ...editingProfile,
                      settings: {
                        ...editingProfile.settings,
                        siteName: e.target.value,
                      },
                    })
                  }
                  className="bg-transparent text-white"
                />
              </Form.Group>

              <h4 className="text-white mb-3 mt-4">Hero Section</h4>
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label className="text-white">Title (EN)</Form.Label>
                    <Form.Control
                      type="text"
                      value={editingProfile?.hero?.title?.en || ""}
                      onChange={(e) =>
                        setEditingProfile({
                          ...editingProfile,
                          hero: {
                            ...editingProfile.hero,
                            title: {
                              ...editingProfile.hero.title,
                              en: e.target.value,
                            },
                          },
                        })
                      }
                      className="bg-transparent text-white"
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label className="text-white">Title (UZ)</Form.Label>
                    <Form.Control
                      type="text"
                      value={editingProfile?.hero?.title?.uz || ""}
                      onChange={(e) =>
                        setEditingProfile({
                          ...editingProfile,
                          hero: {
                            ...editingProfile.hero,
                            title: {
                              ...editingProfile.hero.title,
                              uz: e.target.value,
                            },
                          },
                        })
                      }
                      className="bg-transparent text-white"
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label className="text-white">
                      Name/Subtitle (EN)
                    </Form.Label>
                    <Form.Control
                      type="text"
                      value={editingProfile?.hero?.subtitle?.en || ""}
                      onChange={(e) =>
                        setEditingProfile({
                          ...editingProfile,
                          hero: {
                            ...editingProfile.hero,
                            subtitle: {
                              ...editingProfile.hero.subtitle,
                              en: e.target.value,
                            },
                          },
                        })
                      }
                      className="bg-transparent text-white"
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label className="text-white">
                      Name/Subtitle (UZ)
                    </Form.Label>
                    <Form.Control
                      type="text"
                      value={editingProfile?.hero?.subtitle?.uz || ""}
                      onChange={(e) =>
                        setEditingProfile({
                          ...editingProfile,
                          hero: {
                            ...editingProfile.hero,
                            subtitle: {
                              ...editingProfile.hero.subtitle,
                              uz: e.target.value,
                            },
                          },
                        })
                      }
                      className="bg-transparent text-white"
                    />
                  </Form.Group>
                </Col>
              </Row>

              <h4 className="text-white mb-3 mt-4">Typing Effect Texts</h4>
              <p className="text-muted small mb-3">
                These texts will appear in the typing animation on the homepage
              </p>
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label className="text-white">
                      Typing Texts (EN)
                    </Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={4}
                      value={(editingProfile?.typingTexts?.en || []).join("\n")}
                      onChange={(e) =>
                        setEditingProfile({
                          ...editingProfile,
                          typingTexts: {
                            ...editingProfile.typingTexts,
                            en: e.target.value
                              .split("\n")
                              .filter((t) => t.trim()),
                          },
                        })
                      }
                      placeholder="Frontend Developer&#10;Full Stack Developer&#10;JavaScript Specialist"
                      className="bg-transparent text-white"
                    />
                    <small className="text-muted">One text per line</small>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label className="text-white">
                      Typing Texts (UZ)
                    </Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={4}
                      value={(editingProfile?.typingTexts?.uz || []).join("\n")}
                      onChange={(e) =>
                        setEditingProfile({
                          ...editingProfile,
                          typingTexts: {
                            ...editingProfile.typingTexts,
                            uz: e.target.value
                              .split("\n")
                              .filter((t) => t.trim()),
                          },
                        })
                      }
                      placeholder="Frontend Dasturchi&#10;Full Stack Dasturchi&#10;JavaScript Mutaxassisi"
                      className="bg-transparent text-white"
                    />
                    <small className="text-muted">
                      Har bir matn alohida qatorda
                    </small>
                  </Form.Group>
                </Col>
              </Row>

              <h4 className="text-white mb-3 mt-4">About Section</h4>
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label className="text-white">
                      About Title (EN)
                    </Form.Label>
                    <Form.Control
                      type="text"
                      value={editingProfile?.about?.title?.en || ""}
                      onChange={(e) =>
                        setEditingProfile({
                          ...editingProfile,
                          about: {
                            ...editingProfile.about,
                            title: {
                              ...editingProfile.about.title,
                              en: e.target.value,
                            },
                          },
                        })
                      }
                      placeholder="Who Am I"
                      className="bg-transparent text-white"
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label className="text-white">
                      About Title (UZ)
                    </Form.Label>
                    <Form.Control
                      type="text"
                      value={editingProfile?.about?.title?.uz || ""}
                      onChange={(e) =>
                        setEditingProfile({
                          ...editingProfile,
                          about: {
                            ...editingProfile.about,
                            title: {
                              ...editingProfile.about.title,
                              uz: e.target.value,
                            },
                          },
                        })
                      }
                      placeholder="Men kimman"
                      className="bg-transparent text-white"
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label className="text-white">
                      About Content (EN)
                    </Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={4}
                      value={editingProfile?.about?.content?.en || ""}
                      onChange={(e) =>
                        setEditingProfile({
                          ...editingProfile,
                          about: {
                            ...editingProfile.about,
                            content: {
                              ...editingProfile.about.content,
                              en: e.target.value,
                            },
                          },
                        })
                      }
                      className="bg-transparent text-white"
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label className="text-white">
                      About Content (UZ)
                    </Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={4}
                      value={editingProfile?.about?.content?.uz || ""}
                      onChange={(e) =>
                        setEditingProfile({
                          ...editingProfile,
                          about: {
                            ...editingProfile.about,
                            content: {
                              ...editingProfile.about.content,
                              uz: e.target.value,
                            },
                          },
                        })
                      }
                      className="bg-transparent text-white"
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label className="text-white">Quote (EN)</Form.Label>
                    <Form.Control
                      type="text"
                      value={editingProfile?.about?.quote?.en || ""}
                      onChange={(e) =>
                        setEditingProfile({
                          ...editingProfile,
                          about: {
                            ...editingProfile.about,
                            quote: {
                              ...editingProfile.about.quote,
                              en: e.target.value,
                            },
                          },
                        })
                      }
                      placeholder="Your inspiring quote"
                      className="bg-transparent text-white"
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label className="text-white">Quote (UZ)</Form.Label>
                    <Form.Control
                      type="text"
                      value={editingProfile?.about?.quote?.uz || ""}
                      onChange={(e) =>
                        setEditingProfile({
                          ...editingProfile,
                          about: {
                            ...editingProfile.about,
                            quote: {
                              ...editingProfile.about.quote,
                              uz: e.target.value,
                            },
                          },
                        })
                      }
                      placeholder="Ilhomlantiruvchi iqtibos"
                      className="bg-transparent text-white"
                    />
                  </Form.Group>
                </Col>
              </Row>

              <h4 className="text-white mb-3 mt-4">Social Media Links</h4>
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label className="text-white">GitHub</Form.Label>
                    <Form.Control
                      type="url"
                      value={editingProfile?.social?.github || ""}
                      onChange={(e) =>
                        setEditingProfile({
                          ...editingProfile,
                          social: {
                            ...editingProfile.social,
                            github: e.target.value,
                          },
                        })
                      }
                      className="bg-transparent text-white"
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label className="text-white">LinkedIn</Form.Label>
                    <Form.Control
                      type="url"
                      value={editingProfile?.social?.linkedin || ""}
                      onChange={(e) =>
                        setEditingProfile({
                          ...editingProfile,
                          social: {
                            ...editingProfile.social,
                            linkedin: e.target.value,
                          },
                        })
                      }
                      className="bg-transparent text-white"
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label className="text-white">Twitter</Form.Label>
                    <Form.Control
                      type="url"
                      value={editingProfile?.social?.twitter || ""}
                      onChange={(e) =>
                        setEditingProfile({
                          ...editingProfile,
                          social: {
                            ...editingProfile.social,
                            twitter: e.target.value,
                          },
                        })
                      }
                      className="bg-transparent text-white"
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label className="text-white">Instagram</Form.Label>
                    <Form.Control
                      type="url"
                      value={editingProfile?.social?.instagram || ""}
                      onChange={(e) =>
                        setEditingProfile({
                          ...editingProfile,
                          social: {
                            ...editingProfile.social,
                            instagram: e.target.value,
                          },
                        })
                      }
                      className="bg-transparent text-white"
                    />
                  </Form.Group>
                </Col>
              </Row>

              <h4 className="text-white mb-3 mt-4">Contact Information</h4>
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label className="text-white">Email</Form.Label>
                    <Form.Control
                      type="email"
                      value={editingProfile?.contact?.email || ""}
                      onChange={(e) =>
                        setEditingProfile({
                          ...editingProfile,
                          contact: {
                            ...editingProfile.contact,
                            email: e.target.value,
                          },
                        })
                      }
                      className="bg-transparent text-white"
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label className="text-white">Phone Number</Form.Label>
                    <Form.Control
                      type="tel"
                      value={editingProfile?.contact?.phone || ""}
                      onChange={(e) =>
                        setEditingProfile({
                          ...editingProfile,
                          contact: {
                            ...editingProfile.contact,
                            phone: e.target.value,
                          },
                        })
                      }
                      placeholder="+998 90 123 45 67"
                      className="bg-transparent text-white"
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label className="text-white">Telegram</Form.Label>
                    <Form.Control
                      type="url"
                      value={editingProfile?.contact?.telegram || ""}
                      onChange={(e) =>
                        setEditingProfile({
                          ...editingProfile,
                          contact: {
                            ...editingProfile.contact,
                            telegram: e.target.value,
                          },
                        })
                      }
                      placeholder="https://t.me/username"
                      className="bg-transparent text-white"
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Button type="submit" variant="success" disabled={loading}>
                {loading ? (
                  <Spinner animation="border" size="sm" />
                ) : (
                  "Save Profile"
                )}
              </Button>
            </Form>
          </Tab>

          {/* PROJECTS TAB */}
          <Tab eventKey="projects" title="Projects">
            <Button
              variant="primary"
              className="mb-3"
              onClick={() => navigate("/admin/project/new")}
            >
              + New Project
            </Button>
            <Table striped bordered hover variant="dark">
              <thead>
                <tr>
                  <th>Title (EN)</th>
                  <th>Tech Stack</th>
                  <th>Order</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {data?.projects?.map((project) => (
                  <tr key={project._id}>
                    <td>{project.title?.en || "N/A"}</td>
                    <td>{project.techStack?.join(", ") || "N/A"}</td>
                    <td>{project.order}</td>
                    <td>
                      <Button
                        variant="warning"
                        size="sm"
                        className="me-2"
                        onClick={() =>
                          navigate(`/admin/project/${project._id}`)
                        }
                      >
                        Edit
                      </Button>
                      <Button
                        variant="danger"
                        size="sm"
                        onClick={() => genericDelete("project", project._id)}
                      >
                        Delete
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </Tab>

          {/* BLOGS TAB */}
          <Tab eventKey="blogs" title="Blogs">
            <Button
              variant="primary"
              className="mb-3"
              onClick={() => navigate("/admin/blog/new")}
            >
              + New Blog
            </Button>
            <Table striped bordered hover variant="dark">
              <thead>
                <tr>
                  <th>Title (EN)</th>
                  <th>Published</th>
                  <th>Views</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {data?.blogs?.map((blog) => (
                  <tr key={blog._id}>
                    <td>{blog.title?.en || "N/A"}</td>
                    <td>{blog.published ? "Yes" : "No"}</td>
                    <td>{blog.views || 0}</td>
                    <td>
                      <Button
                        variant="warning"
                        size="sm"
                        className="me-2"
                        onClick={() => navigate(`/admin/blog/${blog._id}`)}
                      >
                        Edit
                      </Button>
                      <Button
                        variant="danger"
                        size="sm"
                        onClick={() => genericDelete("blog", blog._id)}
                      >
                        Delete
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </Tab>

          {/* SKILLS TAB */}
          <Tab eventKey="skills" title="Skills">
            <Button
              variant="primary"
              className="mb-3"
              onClick={() => openSkillModal()}
            >
              + Add Skill
            </Button>
            <Table striped bordered hover variant="dark">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Category</th>
                  <th>Icon</th>
                  <th>Level</th>
                  <th>Order</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {data?.skills?.map((skill) => (
                  <tr key={skill._id}>
                    <td>{skill.name}</td>
                    <td>{skill.category}</td>
                    <td>{skill.icon}</td>
                    <td>{skill.level}%</td>
                    <td>{skill.order}</td>
                    <td>
                      <Button
                        variant="warning"
                        size="sm"
                        className="me-2"
                        onClick={() => openSkillModal(skill)}
                      >
                        Edit
                      </Button>
                      <Button
                        variant="danger"
                        size="sm"
                        onClick={() => genericDelete("skill", skill._id)}
                      >
                        Delete
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </Tab>

          {/* EDUCATION TAB */}
          <Tab eventKey="education" title="Education">
            <Button
              variant="primary"
              className="mb-3"
              onClick={() => openEducationModal()}
            >
              + Add Education
            </Button>
            <Table striped bordered hover variant="dark">
              <thead>
                <tr>
                  <th>Institution</th>
                  <th>Degree (EN)</th>
                  <th>Period</th>
                  <th>Order</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {data?.educations?.map((edu) => (
                  <tr key={edu._id}>
                    <td>{edu.institution}</td>
                    <td>{edu.degree?.en || "N/A"}</td>
                    <td>{edu.period}</td>
                    <td>{edu.order}</td>
                    <td>
                      <Button
                        variant="warning"
                        size="sm"
                        className="me-2"
                        onClick={() => openEducationModal(edu)}
                      >
                        Edit
                      </Button>
                      <Button
                        variant="danger"
                        size="sm"
                        onClick={() => genericDelete("education", edu._id)}
                      >
                        Delete
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </Tab>

          {/* EXPERIENCE TAB */}
          <Tab eventKey="experience" title="Experience">
            <Button
              variant="primary"
              className="mb-3"
              onClick={() => openExperienceModal()}
            >
              + Add Experience
            </Button>
            <Table striped bordered hover variant="dark">
              <thead>
                <tr>
                  <th>Company</th>
                  <th>Role (EN)</th>
                  <th>Period</th>
                  <th>Order</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {data?.experiences?.map((exp) => (
                  <tr key={exp._id}>
                    <td>{exp.company}</td>
                    <td>{exp.role?.en || "N/A"}</td>
                    <td>{exp.period}</td>
                    <td>{exp.order}</td>
                    <td>
                      <Button
                        variant="warning"
                        size="sm"
                        className="me-2"
                        onClick={() => openExperienceModal(exp)}
                      >
                        Edit
                      </Button>
                      <Button
                        variant="danger"
                        size="sm"
                        onClick={() => genericDelete("experience", exp._id)}
                      >
                        Delete
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </Tab>

          {/* MESSAGES TAB */}
          <Tab eventKey="messages" title="Messages">
            <Table striped bordered hover variant="dark">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Message</th>
                  <th>Date</th>
                </tr>
              </thead>
              <tbody>
                {data?.messages?.map((msg) => (
                  <tr key={msg._id}>
                    <td>{msg.name}</td>
                    <td>{msg.email}</td>
                    <td>{msg.message?.substring(0, 50)}...</td>
                    <td>{new Date(msg.createdAt).toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </Tab>
        </Tabs>

        {/* SKILL MODAL */}
        <Modal
          show={showSkillModal}
          onHide={() => setShowSkillModal(false)}
          centered
          className="admin-modal"
        >
          <Modal.Header closeButton className="bg-dark text-white">
            <Modal.Title>
              {editingSkill?._id ? "Edit Skill" : "Add Skill"}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body className="bg-dark">
            <Form onSubmit={handleSkillSave}>
              <Form.Group className="mb-3">
                <Form.Label className="text-white">Skill Name</Form.Label>
                <Form.Control
                  type="text"
                  value={editingSkill?.name || ""}
                  onChange={(e) =>
                    setEditingSkill({ ...editingSkill, name: e.target.value })
                  }
                  className="bg-transparent text-white"
                  required
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label className="text-white">Category</Form.Label>
                <Form.Select
                  value={editingSkill?.category || "Frontend"}
                  onChange={(e) =>
                    setEditingSkill({
                      ...editingSkill,
                      category: e.target.value,
                    })
                  }
                  className="bg-transparent text-white"
                >
                  <option value="Frontend">Frontend</option>
                  <option value="Backend">Backend</option>
                  <option value="Tools">Tools</option>
                  <option value="Security">Security</option>
                </Form.Select>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label className="text-white">
                  Icon (lowercase name)
                </Form.Label>
                <Form.Control
                  type="text"
                  value={editingSkill?.icon || ""}
                  onChange={(e) =>
                    setEditingSkill({ ...editingSkill, icon: e.target.value })
                  }
                  placeholder="react, nodejs, python, etc."
                  className="bg-transparent text-white"
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label className="text-white">Level (0-100)</Form.Label>
                <Form.Control
                  type="number"
                  min="0"
                  max="100"
                  value={editingSkill?.level || 80}
                  onChange={(e) =>
                    setEditingSkill({
                      ...editingSkill,
                      level: parseInt(e.target.value),
                    })
                  }
                  className="bg-transparent text-white"
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label className="text-white">Order</Form.Label>
                <Form.Control
                  type="number"
                  value={editingSkill?.order || 0}
                  onChange={(e) =>
                    setEditingSkill({
                      ...editingSkill,
                      order: parseInt(e.target.value),
                    })
                  }
                  className="bg-transparent text-white"
                />
              </Form.Group>

              <Button type="submit" variant="success" className="w-100">
                Save Skill
              </Button>
            </Form>
          </Modal.Body>
        </Modal>

        {/* EDUCATION MODAL */}
        <Modal
          show={showEducationModal}
          onHide={() => setShowEducationModal(false)}
          centered
          size="lg"
          className="admin-modal"
        >
          <Modal.Header closeButton className="bg-dark text-white">
            <Modal.Title>
              {editingEducation?._id ? "Edit Education" : "Add Education"}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body className="bg-dark">
            <Form onSubmit={handleEducationSave}>
              <Form.Group className="mb-3">
                <Form.Label className="text-white">Institution</Form.Label>
                <Form.Control
                  type="text"
                  value={editingEducation?.institution || ""}
                  onChange={(e) =>
                    setEditingEducation({
                      ...editingEducation,
                      institution: e.target.value,
                    })
                  }
                  className="bg-transparent text-white"
                  required
                />
              </Form.Group>

              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label className="text-white">Degree (EN)</Form.Label>
                    <Form.Control
                      type="text"
                      value={editingEducation?.degree?.en || ""}
                      onChange={(e) =>
                        setEditingEducation({
                          ...editingEducation,
                          degree: {
                            ...editingEducation.degree,
                            en: e.target.value,
                          },
                        })
                      }
                      className="bg-transparent text-white"
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label className="text-white">Degree (UZ)</Form.Label>
                    <Form.Control
                      type="text"
                      value={editingEducation?.degree?.uz || ""}
                      onChange={(e) =>
                        setEditingEducation({
                          ...editingEducation,
                          degree: {
                            ...editingEducation.degree,
                            uz: e.target.value,
                          },
                        })
                      }
                      className="bg-transparent text-white"
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label className="text-white">
                      Description (EN)
                    </Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={3}
                      value={editingEducation?.description?.en || ""}
                      onChange={(e) =>
                        setEditingEducation({
                          ...editingEducation,
                          description: {
                            ...editingEducation.description,
                            en: e.target.value,
                          },
                        })
                      }
                      className="bg-transparent text-white"
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label className="text-white">
                      Description (UZ)
                    </Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={3}
                      value={editingEducation?.description?.uz || ""}
                      onChange={(e) =>
                        setEditingEducation({
                          ...editingEducation,
                          description: {
                            ...editingEducation.description,
                            uz: e.target.value,
                          },
                        })
                      }
                      className="bg-transparent text-white"
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label className="text-white">Period</Form.Label>
                    <Form.Control
                      type="text"
                      value={editingEducation?.period || ""}
                      onChange={(e) =>
                        setEditingEducation({
                          ...editingEducation,
                          period: e.target.value,
                        })
                      }
                      placeholder="Sep 2024 - Sep 2029"
                      className="bg-transparent text-white"
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label className="text-white">Order</Form.Label>
                    <Form.Control
                      type="number"
                      value={editingEducation?.order || 0}
                      onChange={(e) =>
                        setEditingEducation({
                          ...editingEducation,
                          order: parseInt(e.target.value),
                        })
                      }
                      className="bg-transparent text-white"
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Button type="submit" variant="success" className="w-100">
                Save Education
              </Button>
            </Form>
          </Modal.Body>
        </Modal>

        {/* EXPERIENCE MODAL */}
        <Modal
          show={showExperienceModal}
          onHide={() => setShowExperienceModal(false)}
          centered
          size="lg"
          className="admin-modal"
        >
          <Modal.Header closeButton className="bg-dark text-white">
            <Modal.Title>
              {editingExperience?._id ? "Edit Experience" : "Add Experience"}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body className="bg-dark">
            <Form onSubmit={handleExperienceSave}>
              <Form.Group className="mb-3">
                <Form.Label className="text-white">Company</Form.Label>
                <Form.Control
                  type="text"
                  value={editingExperience?.company || ""}
                  onChange={(e) =>
                    setEditingExperience({
                      ...editingExperience,
                      company: e.target.value,
                    })
                  }
                  className="bg-transparent text-white"
                  required
                />
              </Form.Group>

              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label className="text-white">Role (EN)</Form.Label>
                    <Form.Control
                      type="text"
                      value={editingExperience?.role?.en || ""}
                      onChange={(e) =>
                        setEditingExperience({
                          ...editingExperience,
                          role: {
                            ...editingExperience.role,
                            en: e.target.value,
                          },
                        })
                      }
                      className="bg-transparent text-white"
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label className="text-white">Role (UZ)</Form.Label>
                    <Form.Control
                      type="text"
                      value={editingExperience?.role?.uz || ""}
                      onChange={(e) =>
                        setEditingExperience({
                          ...editingExperience,
                          role: {
                            ...editingExperience.role,
                            uz: e.target.value,
                          },
                        })
                      }
                      className="bg-transparent text-white"
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label className="text-white">
                      Description (EN)
                    </Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={4}
                      value={editingExperience?.description?.en || ""}
                      onChange={(e) =>
                        setEditingExperience({
                          ...editingExperience,
                          description: {
                            ...editingExperience.description,
                            en: e.target.value,
                          },
                        })
                      }
                      className="bg-transparent text-white"
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label className="text-white">
                      Description (UZ)
                    </Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={4}
                      value={editingExperience?.description?.uz || ""}
                      onChange={(e) =>
                        setEditingExperience({
                          ...editingExperience,
                          description: {
                            ...editingExperience.description,
                            uz: e.target.value,
                          },
                        })
                      }
                      className="bg-transparent text-white"
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label className="text-white">Period</Form.Label>
                    <Form.Control
                      type="text"
                      value={editingExperience?.period || ""}
                      onChange={(e) =>
                        setEditingExperience({
                          ...editingExperience,
                          period: e.target.value,
                        })
                      }
                      placeholder="Jul 2025 - Present"
                      className="bg-transparent text-white"
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label className="text-white">Order</Form.Label>
                    <Form.Control
                      type="number"
                      value={editingExperience?.order || 0}
                      onChange={(e) =>
                        setEditingExperience({
                          ...editingExperience,
                          order: parseInt(e.target.value),
                        })
                      }
                      className="bg-transparent text-white"
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Button type="submit" variant="success" className="w-100">
                Save Experience
              </Button>
            </Form>
          </Modal.Body>
        </Modal>
      </Container>
    </Container>
  );
}

export default AdminPanel;
